/**
 * Enhanced Multi-Select Component for better UX
 * Addresses issues with selection visibility and deselection clarity
 */

import React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
  Checkbox,
  Typography,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

// Styled components for better visual distinction
const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiSelect-select": {
    paddingTop: "14px",
    paddingBottom: "14px",
    minHeight: "20px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.grey[300],
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  minHeight: "48px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&.Mui-selected": {
    backgroundColor: `${theme.palette.primary.main}15 !important`, // Light primary color with transparency
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}25 !important`,
    },
  },
  "& .MuiCheckbox-root": {
    padding: "4px",
    marginRight: "8px",
  },
}));

const SelectionChip = styled(Chip)(({ theme }) => ({
  margin: "2px",
  height: "24px",
  fontSize: "0.75rem",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  "& .MuiChip-deleteIcon": {
    color: theme.palette.primary.contrastText,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

const CountChip = styled(Chip)(({ theme }) => ({
  margin: "2px",
  height: "24px",
  fontSize: "0.75rem",
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  cursor: "default",
}));

const MDMultiSelect = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder,
  disabled = false,
  error = false,
  helperText,
  maxDisplayChips = 2,
  renderChips = true,
  fullWidth = true,
  variant = "outlined",
  size = "medium",
  MenuProps = {},
  ...props
}) => {
  const selectedValues = Array.isArray(value) ? value : [];

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange(event);
    }
  };

  const handleChipDelete = (chipValue) => {
    const newValue = selectedValues.filter((val) => val !== chipValue);
    const event = {
      target: { value: newValue },
    };
    onChange(event);
  };

  const renderValue = (selected) => {
    if (selected.length === 0) {
      return (
        <Typography variant="body1" color="textSecondary">
          {placeholder || `Select ${label}`}
        </Typography>
      );
    }

    if (!renderChips) {
      const selectedLabels = selected
        .map((val) => {
          const option = options.find((opt) => opt.value === val);
          return option ? option.label : val;
        })
        .filter(Boolean);
      
      if (selectedLabels.length <= 3) {
        return selectedLabels.join(", ");
      }
      return `${selectedLabels.slice(0, 2).join(", ")}, +${selectedLabels.length - 2} more`;
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}>
        {selected.slice(0, maxDisplayChips).map((val) => {
          const option = options.find((opt) => opt.value === val);
          const label = option ? option.label : val;
          return (
            <SelectionChip
              key={val}
              label={label}
              onDelete={() => handleChipDelete(val)}
              size="small"
              variant="filled"
            />
          );
        })}
        {selected.length > maxDisplayChips && (
          <CountChip
            label={`+${selected.length - maxDisplayChips} more`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    );
  };

  const defaultMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        borderRadius: 8,
        marginTop: 4,
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
      },
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    ...MenuProps,
  };

  return (
    <FormControl fullWidth={fullWidth} variant={variant} error={error} disabled={disabled}>
      <InputLabel id={`multiselect-${label}-label`} shrink={selectedValues.length > 0}>
        {label}
      </InputLabel>
      <StyledSelect
        labelId={`multiselect-${label}-label`}
        multiple
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        MenuProps={defaultMenuProps}
        size={size}
        displayEmpty
        {...props}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <StyledMenuItem key={option.value} value={option.value}>
              <Checkbox
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                checked={isSelected}
                color="primary"
                size="small"
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? "primary.main" : "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.label}
                </Typography>
                {option.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {option.subtitle}
                  </Typography>
                )}
              </Box>
            </StyledMenuItem>
          );
        })}
        {options.length === 0 && (
          <MenuItem disabled>
            <Typography color="text.secondary">No options available</Typography>
          </MenuItem>
        )}
      </StyledSelect>
      {helperText && (
        <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.75 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

MDMultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
    })
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  maxDisplayChips: PropTypes.number,
  renderChips: PropTypes.bool,
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  size: PropTypes.oneOf(["small", "medium"]),
  MenuProps: PropTypes.object,
};

export default MDMultiSelect;